<script setup lang="ts">
  import { Tag } from "~/entities/Tag";
  import { getPublishStatusLabel } from "~/consts/contentsConfig";
  import { useHistoryStore } from "~/stores/HistoryStore";

  interface Props {
    startDateTime?: string;
    tags?: Tag[];
    image?: string;
    title?: string;
    publishedText?: string;
    publishDateTime?: string;
    startText?: string;
    isFree?: boolean;
    toPath?: string;
    loading?: boolean;
    publishStatus?: string;
    moviePlayTimeSec?: number;
    contentId?: string;
    contentType?: string;
    isUnread?: boolean;
    goodCount?: number;
    settingPath?: string;
    documentTotalPage?: number;
    isGood?: boolean;
    description?: string;
    isBookmark?: boolean;
    spFullWidth?: boolean;
    height?: number;
    spHeight?: number;
  }
  const props = defineProps<Props>();
  const historyStore = useHistoryStore();

  const elapsedDateTimeText = computed<string>(() =>
    props.publishDateTime ? `${GetElapsedText(props.publishDateTime)}に投稿` : " ",
  );
  const onClickSetting = () => {
    if (!props.settingPath) return;
    historyStore.to(props.settingPath);
  };

  const publishStatusText = computed<string>(() => {
    // 公開は非表示
    if (props.publishStatus === "PUBLISH") return "";
    return getPublishStatusLabel(props.publishStatus ?? "");
  });
  const isTable = isTabletAndSpDisplayRef();

  const cardHeight = computed(() => {
    return (props.height ?? 145) + "px";
  });

  const spCardHeight = computed(() => {
    return (props.spHeight ?? 220) + "px";
  });

  const contentWrapper = ref<HTMLDivElement>();
  const contentWrapperHeight = computed(() => {
    return contentWrapper.value?.clientHeight ?? 0;
  });
  const fullSpCardHeight = computed(() => {
    return (props.spHeight ?? 220) + contentWrapperHeight.value + 20 + "px";
  });
</script>

<template>
  <v-badge
    class="badge"
    :class="{ 'sp-small-width': !spFullWidth }"
    :color="isUnread ? 'red' : 'transparent'"
    :offset-x="12"
    :offset-y="12"
  >
    <v-card
      class="pa-0 ma-0 card-wrapper"
      color="transparent"
      variant="flat"
      link
      nuxt
      :to="toPath"
      :ripple="false"
      data-testid="base-featured-card"
    >
      <div class="wrapper ga-3">
        <div class="left-wrapper">
          <!-- 画像 -->
          <div style="position: relative">
            <AtomsImgWithLoading
              class="w-100 rounded"
              :height="isTable ? spCardHeight : cardHeight"
              :src="image"
              contains
            />
            <v-sheet v-if="moviePlayTimeSec" class="rounded-pill movie-play-time text-white">{{
              formatMoviePlayTime(moviePlayTimeSec)
            }}</v-sheet>
            <v-sheet v-if="startDateTime" class="live-time" color="transparent">
              <AtomsLiveTimer :start-date-time="startDateTime" />
            </v-sheet>
            <v-sheet v-if="documentTotalPage" class="rounded-pill document-total-page text-white">{{
              `${documentTotalPage}ページ`
            }}</v-sheet>
          </div>
        </div>

        <div ref="contentWrapper" class="content-wrapper">
          <div class="d-flex space-between align-center">
            <v-sheet class="mx-0 my-2 text-free d-flex justify-start align-end" color="transparent">
              <v-icon v-if="!isFree" class="mr-1" :size="20" icon="$mdiLock" />
              <span>{{ isFree ? "無料公開" : "会員限定" }}</span>
            </v-sheet>
            <div class="flex-grow-1"></div>
            <v-sheet
              v-if="publishStatusText"
              class="rounded-pill py-1 px-2 text-center"
              :class="{ 'mr-8': isUnread && !isTable }"
              color="monochrome-06"
            >
              {{ publishStatusText }}
            </v-sheet>
          </div>

          <div class="d-flex align-center">
            <!-- 配信時間 -->
            <v-sheet v-if="publishedText" class="w-100 py-2 rounded mb-1" color="transparent">
              <p class="text-start-date text-center">{{ publishedText }}</p>
            </v-sheet>
            <v-sheet v-if="startText" class="w-100 py-2 rounded mb-1" color="main-01">
              <p class="text-start-date text-center">{{ startText }}</p>
            </v-sheet>
            <v-sheet v-if="startDateTime" class="w-100 py-2 rounded mb-1" color="main-02">
              <p class="text-start-date text-center">ライブ配信中</p>
            </v-sheet>
          </div>

          <v-sheet class="ma-0 text-content-card-l font-weight-bold card-title" color="transparent">
            {{ title }}
          </v-sheet>

          <div class="d-flex align-center">
            <v-sheet v-if="elapsedDateTimeText.trim() !== ''" class="py-1 pr-2" color="transparent">
              <p class="ma-0 text-publish-date text-right text-monochrome-02">
                {{ elapsedDateTimeText }}
              </p>
            </v-sheet>

            <template v-if="goodCount !== undefined">
              <AtomsGoodButton
                class="good-count-button"
                :is-gooded="isGood ?? false"
                :count="goodCount"
                :clickable="false"
                color="monochrome-02"
              />
            </template>
            <MoleculesBookmarkButton
              :content-id="contentId"
              :is-bookmark="!!isBookmark"
              :clickable="true"
              :hide-bg-color="true"
            />
          </div>
          <div class="featured-description">
            {{ omitString(description, 100) }}
          </div>
        </div>
      </div>
    </v-card>

    <v-icon
      v-if="settingPath"
      icon="$mdiCogOutline"
      class="setting-button text-monochrome-02"
      :size="22"
      :data-testid="`content-setting-button-${contentId ?? ''}`"
      @click.stop="onClickSetting"
    />
  </v-badge>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .badge {
    width: 100%;
  }
  .sp-small-width {
    @include display_sp {
      width: 330px;
    }
  }
  .card-wrapper {
    position: relative;
    height: v-bind(cardHeight);
    width: 100%;
    @include display_sp {
      height: v-bind(fullSpCardHeight);
    }
  }
  .card-title {
    overflow: hidden;
    display: -webkit-box;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  .publish-status {
    position: absolute;
    z-index: 1;
  }
  .movie-play-time,
  .document-total-page {
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: #22222280;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px; // レイアウトが特殊なので個別指定
    letter-spacing: 0.06em;
    padding: 0 12px;
    margin: 0 4px 5px 0;
  }

  .live-time {
    position: absolute;
    bottom: 0;
    right: 0;
    margin: 0 4px 5px 0;
  }

  .text-start-date {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px; // レイアウトが特殊なので個別指定
  }

  .text-publish-date {
    font-size: 14px;
    font-weight: bold;
    line-height: 22px; // レイアウトが特殊なので個別指定
    letter-spacing: 0.06em;
  }

  .text-free {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px; // レイアウトが特殊なので個別指定
    letter-spacing: 0.06em;
  }

  .good-count-button {
    padding-left: 0;
    background-color: transparent;
  }

  .setting-button {
    position: absolute;
    // top: 0;
    bottom: 0;
    right: 8px;
    margin: auto 0;
    cursor: pointer;
  }

  .wrapper {
    display: flex;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    padding: 0;
    @include display_sp {
      flex-direction: column;
      height: v-bind(spCardHeight);
    }
  }
  .left-wrapper {
    flex-shrink: 0;
    width: 220px;
    @include display_sp {
      height: 100%;
      width: 100%;
    }
  }
  .content-wrapper {
    width: 100%;
    flex-grow: 1;
    text-align: left;
    @include display_sp {
      padding-left: 8px;
    }
  }
  .featured-description {
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    color: rgb(var(--v-theme-monochrome-02));
    margin: 0 0 10px 0;
  }
</style>
